import { useEffect, useRef, useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import SuggestInput from "@/components/app/SuggestInput";

// Click-to-edit for a single field: the read-only presentation is a button; activating it swaps in the matching input.
// Enter saves (Ctrl/Cmd+Enter for textareas), Escape cancels, blur saves when the value changed. Teams use explicit Save/Cancel because chips steal focus.
export default function InlineField({ label, children, value, type = "text", onSave, canEdit, testid, inputClass = "pp-input", min, max, options = [], className = "" }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (!editing) setV(value ?? ""); }, [value, editing]);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); if (type !== "date" && ref.current.select) ref.current.select(); } }, [editing, type]);
  const cancel = () => { setV(value ?? ""); setEditing(false); };
  const commit = async () => {
    if (busy) return;
    if (String(v ?? "") === String(value ?? "")) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(v); toast.success(`${label} saved`); setEditing(false); }
    catch (e) { toast.error(errMsg(e) || e.message); ref.current?.focus(); }
    finally { setBusy(false); }
  };
  const keys = (e) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
    else if (e.key === "Enter" && (type !== "textarea" || e.ctrlKey || e.metaKey) && type !== "teams") { e.preventDefault(); commit(); }
  };
  if (!canEdit) return <div className={className}><div className="pp-label">{label}</div>{children}</div>;
  if (!editing) {
    return (
      <div className={className}>
        <div className="pp-label">{label}</div>
        <button type="button" data-testid={`inline-${testid}`} aria-label={`Edit ${label}`} onClick={() => setEditing(true)}
          className="group relative w-full text-left rounded-lg -mx-1.5 px-1.5 py-0.5 transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-text">
          {children}
          <Pencil size={12} aria-hidden="true" className="absolute right-1 top-1 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
        </button>
      </div>
    );
  }
  const common = { ref, "data-testid": `inline-${testid}-input`, "aria-label": label, value: v, onKeyDown: keys, disabled: busy, className: inputClass };
  return (
    <div className={className} data-testid={`inline-${testid}-editing`}>
      <div className="pp-label">{label} <span className="normal-case tracking-normal font-normal text-muted-foreground">· {type === "teams" ? "Save or Esc" : type === "textarea" ? "Ctrl+Enter to save · Esc to cancel" : "Enter to save · Esc to cancel"}</span></div>
      {type === "textarea" ? <textarea {...common} onChange={(e) => setV(e.target.value)} onBlur={commit} className={`${inputClass} min-h-[90px]`} />
        : type === "teams" ? (
          <div>
            <SuggestInput testid="edit-teams" mode="list" value={v} onChange={setV} options={options} inputProps={{ ...common, onBlur: undefined, "aria-label": `${label} — tap known teams or type your own, comma-separated` }} />
            <div className="flex gap-2 mt-2"><button type="button" data-testid={`inline-${testid}-save`} onClick={commit} disabled={busy} className="pp-btn-dark text-xs py-1"><Check size={12} /> Save</button><button type="button" onClick={cancel} className="pp-btn-ghost text-xs py-1"><X size={12} /> Cancel</button></div>
          </div>
        ) : <input {...common} type={type} min={min} max={max} onChange={(e) => setV(e.target.value)} onBlur={commit} />}
    </div>
  );
}
